import React from 'react';
import { useLocation } from 'react-router';
import { Link, useHistory } from 'react-router-dom';
import { CaretUpOutlined, CaretDownOutlined, UserOutlined, FileOutlined, LeftSquareFilled } from '@ant-design/icons';
import logout from '@assets/images/logout.png';
import logo from '@assets/images/sage-logo-login.png';
import styles from './style.module.scss';
import { isAccessibleRoute, MenuItem, navigation, Navigation } from '@constants/Navigation';
import cn from 'classnames';
import { AppContext } from '@layouts/AppRouter';
import auth from '@services/api/auth';

interface MenuProps {
  menu: MenuItem[];
  className?: string;
}

const Menu: React.FC<MenuProps> = ({ menu, className }) => {
  const { settings } = React.useContext(AppContext);

  const filteredMenu = menu.filter((item) => {
    if (item.label === 'Rescue' && !settings?.enableRescue) return false;
    if (item.label === 'Tags' && !settings?.enableTags) return false;
    if (item.label === 'Request Tracking' && !settings?.enableTracking) return false;
    return true;
  });

  return (
    <ul className={className}>
      {filteredMenu.map((item) => (
        <Item item={item} key={item.label} />
      ))}
    </ul>
  );
};

const Item = ({ item }: { item: MenuItem }) => {
  const { pathname } = useLocation();
  const { user } = React.useContext(AppContext);
  const isCollapsed = !pathname.includes(item.link);

  if (!isAccessibleRoute(user, item.link)) {
    return null;
  }

  return (
    <li>
      <div className={styles.linkBlock}>
        <Link
          className={cn(pathname === item.link && !item.subItems?.length && styles.active, styles.link)}
          to={item.link}
        >
          {item.label}
        </Link>
        {item.subItems && (
          <span style={{ cursor: 'pointer', zIndex: '10' }}>
            {isCollapsed ? <CaretDownOutlined /> : <CaretUpOutlined />}
          </span>
        )}
      </div>

      {item.subItems && !isCollapsed && <Menu menu={item.subItems} />}
    </li>
  );
};

const SideBarMenu: React.FC = () => {
  const appContext = React.useContext(AppContext);
  const history = useHistory();

  const logoutHandler = async () => {
    try {
      history.push(Navigation.Login);
      await auth.logOut();
      appContext.setToken(null);
    } catch (error) {
      console.log(error);
    } finally {
      appContext.setUser(null);
    }
  };

  return (
    <nav className={cn(appContext.hideSideBar && styles.sidebarHide, styles.sidebar)}>
      <div className={styles.hideLogo}>
        <LeftSquareFilled
          style={{ color: '#1890ff', fontSize: '25px', cursor: 'pointer' }}
          onClick={() => {
            appContext.setHideSideBar(true);
          }}
        />
      </div>
      {isAccessibleRoute(appContext.user, Navigation.Dashboard) ? (
        <Link to={Navigation.Dashboard}>
          <img src={logo} alt="logo" className={styles.logo} />
        </Link>
      ) : (
        <img src={logo} alt="logo" className={styles.logo} />
      )}
      <Menu menu={navigation} className={styles.menu} />
      <div className={styles.footer}>
        <div className={styles.documentation}>
          <FileOutlined style={{ fontSize: '28px', color: '#fff' }} />
          <a target="_blank" href="/static/documentation/SOTA_User_Manual.html">
            Documentation
          </a>
        </div>
        {appContext.user && (
          <div onClick={() => history.push(Navigation.MyProfile)} className={styles.profileBlock}>
            <UserOutlined style={{ fontSize: '28px' }} />
            <span>
              {appContext.user.firstName || ''} {appContext.user.lastName || ''}
            </span>
          </div>
        )}
        <div className={styles.logoutBlock} onClick={logoutHandler}>
          <img src={logout} alt="Logout Icon" />
          <span>Log Out</span>
        </div>
      </div>
    </nav>
  );
};

export default React.memo(SideBarMenu);
